import * as i0 from '@angular/core';
import { EventEmitter, Directive, Self, Optional, Inject, Output, Input } from '@angular/core';
import * as i1 from '@angular/forms';
import { NgControl } from '@angular/forms';
import { Subject, merge } from 'rxjs';
import { map, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CoerceBoolean } from '@slidebean/hydra/coercion';

function NOOP() { }
const ControlStatus = {
    VALID: 'VALID',
    INVALID: 'INVALID',
    PENDING: 'PENDING',
    DISABLED: 'DISABLED',
};
class Control {
    constructor(control, detector) {
        this.control = control;
        this.detector = detector;
        this.onChange = NOOP;
        this.onTouched = NOOP;
        this.isFocused = false;
        this.currentValue = this.getDefaultValue();
        this.destroy$ = new Subject();
        this.valueEmitter$ = new Subject();
        this.statusEmitter$ = new Subject();
        this.value$ = this.valueEmitter$.asObservable();
        this.status$ = this.statusEmitter$.asObservable();
        this.disabled$ = this.status$.pipe(map(status => status === ControlStatus.DISABLED), distinctUntilChanged());
        this.blured = new EventEmitter();
        this.focused = new EventEmitter();
        if (!!this.control)
            this.control.valueAccessor = this;
        else
            console.warn(`NgControl was not injected in ${this.constructor.name} \n`, 'Use [(ngModel)], [formControl] or formControlName for it to work correctly.');
    }
    get disabled() {
        return this.control?.disabled ?? false;
    }
    get value() {
        return this.currentValue ?? this.getDefaultValue();
    }
    get invalid() {
        return this.control?.invalid ?? false;
    }
    get valid() {
        return this.control?.valid ?? false;
    }
    get touched() {
        return this.control?.touched ?? false;
    }
    get dirty() {
        return this.control?.dirty ?? false;
    }
    get pristine() {
        return this.control?.pristine ?? false;
    }
    get status() {
        return (this.control?.status ?? ControlStatus.VALID);
    }
    ngOnInit() {
        if (!this.control ||
            !this.control.valueChanges ||
            !this.control.statusChanges)
            return;
        this.currentValue = this.control.value ?? this.getDefaultValue();
        merge(this.control.valueChanges, this.control.statusChanges)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.syncLocalValue(this.control?.value));
        this.control.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => this.valueEmitter$.next(value));
        this.control.statusChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(status => this.statusEmitter$.next(status));
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    writeValue(raw) {
        const value = raw ?? this.getDefaultValue();
        if (this.didValueChange(this.value, value))
            return;
        this.syncLocalValue(value);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState() {
        this.checkControlChanges();
    }
    notifyFocus() {
        if (this.isFocused)
            return;
        this.isFocused = true;
        this.focused.emit();
    }
    notifyBlur() {
        if (!this.isFocused)
            return;
        this.isFocused = false;
        if (!!this.onTouched) {
            this.onTouched();
            this.checkControlChanges();
        }
        this.blured.emit();
    }
    setValue(raw) {
        const value = raw ?? this.getDefaultValue();
        if (!this.didValueChange(this.value, value))
            return;
        if (!!this.onChange)
            this.onChange(value);
        this.syncLocalValue(value);
    }
    didValueChange(left, right) {
        return left !== right;
    }
    syncLocalValue(value) {
        this.currentValue = value ?? this.getDefaultValue();
        this.checkControlChanges();
    }
    checkControlChanges() {
        this.detector.detectChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Control, deps: [{ token: NgControl, optional: true, self: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: Control, outputs: { blured: "blured", focused: "focused" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Control, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: i1.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [NgControl]
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { blured: [{
                type: Output
            }], focused: [{
                type: Output
            }] } });

var __decorate$1 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class TextInputPrimitive {
    constructor() {
        this.value = '';
        this.autofocus = false;
        this.readonly = false;
        this.disabled = false;
        this.changed = new EventEmitter();
        this.focused = new EventEmitter();
        this.blured = new EventEmitter();
    }
    notifyFocus() {
        this.focused.emit();
    }
    notifyBlur() {
        this.blured.emit();
    }
    setValue(raw) {
        const value = raw ?? '';
        if (this.value === value)
            return;
        this.value = value;
        this.changed.emit(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextInputPrimitive, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: TextInputPrimitive, inputs: { id: ["html-id", "id"], name: "name", value: "value", placeholder: "placeholder", tabIndex: "tabIndex", autofocus: "autofocus", readonly: "readonly", autocomplete: "autocomplete", disabled: "disabled", minlength: "minlength", maxlength: "maxlength" }, outputs: { changed: "changed", focused: "focused", blured: "blured" }, ngImport: i0 }); }
}
__decorate$1([
    CoerceBoolean()
], TextInputPrimitive.prototype, "disabled", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextInputPrimitive, decorators: [{
            type: Directive
        }], propDecorators: { id: [{
                type: Input,
                args: ['html-id']
            }], name: [{
                type: Input
            }], value: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], autofocus: [{
                type: Input
            }], readonly: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], disabled: [{
                type: Input
            }], minlength: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], changed: [{
                type: Output
            }], focused: [{
                type: Output
            }], blured: [{
                type: Output
            }] } });

var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class CheckInputPrimitive {
    constructor() {
        this.checked = false;
        this.autofocus = false;
        this.disabled = false;
        this.changed = new EventEmitter();
        this.focused = new EventEmitter();
        this.blured = new EventEmitter();
    }
    notifyFocus() {
        this.focused.emit();
    }
    notifyBlur() {
        this.blured.emit();
    }
    setValue(checked) {
        if (this.checked === checked)
            return;
        this.checked = checked;
        this.changed.emit(checked);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckInputPrimitive, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: CheckInputPrimitive, inputs: { id: ["html-id", "id"], name: "name", checked: "checked", tabIndex: "tabIndex", autofocus: "autofocus", disabled: "disabled" }, outputs: { changed: "changed", focused: "focused", blured: "blured" }, ngImport: i0 }); }
}
__decorate([
    CoerceBoolean()
], CheckInputPrimitive.prototype, "checked", void 0);
__decorate([
    CoerceBoolean()
], CheckInputPrimitive.prototype, "disabled", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckInputPrimitive, decorators: [{
            type: Directive
        }], propDecorators: { id: [{
                type: Input,
                args: ['html-id']
            }], name: [{
                type: Input
            }], checked: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], autofocus: [{
                type: Input
            }], disabled: [{
                type: Input
            }], changed: [{
                type: Output
            }], focused: [{
                type: Output
            }], blured: [{
                type: Output
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { CheckInputPrimitive, Control, ControlStatus, TextInputPrimitive };
